import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper,
  IconButton,
  ListSubheader
} from '@mui/material';
import {
  Edit,
  ExpandMore,
  AccountBalance,
  TrendingUp,
  DateRange,
  Lock,
  SwapHoriz,
  Gavel,
  Description
} from '@mui/icons-material';
import { api as axios } from '../../api';
import url from '../../serverUrl';
import { logoWrapper } from '../../utils';
import TreasuryLockStatusFields from './TreasuryLockStatusFields';

import {
  YIELD_RATE_CODES,
  SENIORITY_CODES,
  REDEMPTION_FREQUENCY_CODES,
  YIELD_TYPE_CODES,
  MANDATORY_HOLD_CODES,
  EARLY_REDEMPTION_CODES,
  YTM_CODES,
  CONVERSION_WINDOW_CODES,
  CONVERSION_MECHANISM_CODES,
  GRADE_REFERENCE_CODES,
  EXEMPTION_REFERENCE_CODES,
  REGION_REFERENCE_CODES,
  SECTOR_REFERENCE_CODES,
  REFERENCE_SYMBOL_CODES
} from './dcnCodeDictionary';

function EditDCNToken({ token, open, setOpen }) {
  /* ===================== STATE ===================== */
  const [form, setForm] = useState(token);
  const [status, setStatus] = useState(token?.status ?? false);
  const [logo, setLogo] = useState(token?.logo ?? '');

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    setForm(token);
    setStatus(token?.status ?? false);
    setLogo(token?.logo ?? '');
  }, [token]);

  /* ===================== HELPER FUNCTIONS ===================== */

  // Helper function to get label from code
  const getLabelFromCode = (code, codeArray) => {
    if (!code) return '';
    const item = codeArray.find((c) => c.code === code);
    return item ? item.label : '';
  };

  // Helper function to get code from label
  const labelToCode = (label, codeArray) => {
    if (!label) return '';
    const item = codeArray.find((c) => c.label === label);
    return item ? item.code : '';
  };

  /* ===================== DERIVED VALUES (HOOK SAFE) ===================== */

  const hourlyYield = useMemo(() => {
    if (!form?.annual_yield) return '0.000000';
    return (Number(form.annual_yield) / 365 / 24).toFixed(6);
  }, [form?.annual_yield]);

  const dailyYield = useMemo(() => {
    return (Number(hourlyYield) * 24).toFixed(6);
  }, [hourlyYield]);

  const yieldToMaturity = useMemo(() => {
    if (form?.type === 'PDCN') return 'PERP';

    if (
      form?.type === 'FDCN' &&
      form?.annual_yield &&
      form?.face_value &&
      form?.maturity_date
    ) {
      const years =
        (new Date(form.maturity_date) - new Date()) /
        (1000 * 60 * 60 * 24 * 365);

      if (years <= 0) return '';

      const gain =
        (Number(form.annual_yield) / 100) * years * Number(form.face_value);

      return (Number(form.face_value) + gain).toFixed(2);
    }

    return '';
  }, [form?.type, form?.annual_yield, form?.face_value, form?.maturity_date]);

  /* ===================== EARLY RETURN ===================== */

  /* ===================== HANDLERS ===================== */
  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const saveToken = () => {
    // Convert codes to labels before sending
    const dataToSend = {
      ...form,
      status,
      // Convert all code fields to their label values
      yield_type_code: getLabelFromCode(form.yield_type_code, YIELD_TYPE_CODES),
      yield_rate_code: getLabelFromCode(form.yield_rate_code, YIELD_RATE_CODES),
      yield_redemption_code: getLabelFromCode(
        form.yield_redemption_code,
        REDEMPTION_FREQUENCY_CODES
      ),
      ytm_type_code: getLabelFromCode(form.ytm_type_code, YTM_CODES),
      seniority_code: getLabelFromCode(form.seniority_code, SENIORITY_CODES),
      mandatory_hold_code: getLabelFromCode(
        form.mandatory_hold_code,
        MANDATORY_HOLD_CODES
      ),
      early_redemption_code: getLabelFromCode(
        form.early_redemption_code,
        EARLY_REDEMPTION_CODES
      ),
      conversion_mechanism_code: getLabelFromCode(
        form.conversion_mechanism_code,
        CONVERSION_MECHANISM_CODES
      ),
      conversion_type_code: getLabelFromCode(
        form.conversion_type_code,
        CONVERSION_WINDOW_CODES
      ),
      grade_reference: getLabelFromCode(
        form.grade_reference,
        GRADE_REFERENCE_CODES
      ),
      exemption_reference: getLabelFromCode(
        form.exemption_reference,
        EXEMPTION_REFERENCE_CODES
      ),
      region_reference: getLabelFromCode(
        form.region_reference,
        REGION_REFERENCE_CODES
      ),
      sector_reference: getLabelFromCode(
        form.sector_reference,
        SECTOR_REFERENCE_CODES
      ),
      reference_symbol: form.reference_symbol
    };

    axios
      .put(`${url}/crypto/updateToken`, dataToSend)
      .then((res) => alert(res.data.message))
      .catch((err) => alert(JSON.stringify(err)));
  };

  const uploadLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('image', file);
    fd.append('fileName', file.name);

    axios.post(`${url}/hootdex/token-logo-upload`, fd).then((res) => {
      if (res.data.status === 'ok') {
        const img = `${url}/hootdex/images/${res.data.fileName}`;
        setLogo(img);
        setForm({ ...form, logo: img });
      }
    });
  };

  useEffect(() => {
    if (!token) return;

    setForm({
      ...token,

      // ---- NORMALIZE LABEL → CODE ----
      yield_type_code: labelToCode(token.yield_type_code, YIELD_TYPE_CODES),
      yield_rate_code: labelToCode(token.yield_rate_code, YIELD_RATE_CODES),
      yield_redemption_code: labelToCode(
        token.yield_redemption_code,
        REDEMPTION_FREQUENCY_CODES
      ),
      ytm_type_code: labelToCode(token.ytm_type_code, YTM_CODES),
      seniority_code: labelToCode(token.seniority_code, SENIORITY_CODES),
      mandatory_hold_code: labelToCode(
        token.mandatory_hold_code,
        MANDATORY_HOLD_CODES
      ),
      early_redemption_code: labelToCode(
        token.early_redemption_code,
        EARLY_REDEMPTION_CODES
      ),
      conversion_mechanism_code: labelToCode(
        token.conversion_mechanism_code,
        CONVERSION_MECHANISM_CODES
      ),
      conversion_type_code: labelToCode(
        token.conversion_type_code,
        CONVERSION_WINDOW_CODES
      ),
      grade_reference: labelToCode(
        token.grade_reference,
        GRADE_REFERENCE_CODES
      ),
      exemption_reference: labelToCode(
        token.exemption_reference,
        EXEMPTION_REFERENCE_CODES
      ),
      region_reference: labelToCode(
        token.region_reference,
        REGION_REFERENCE_CODES
      ),
      sector_reference: labelToCode(
        token.sector_reference,
        SECTOR_REFERENCE_CODES
      )
    });

    setStatus(Boolean(token.status));
    setLogo(token.logo ?? '');
  }, [token]);

  /* ===================== JSX ===================== */
  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'success.dark', color: 'white', py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={600}>
            Edit DCN Token
          </Typography>
          <Chip
            label={status ? 'Active' : 'Inactive'}
            color={status ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#f8f9fa', p: 0 }}>
        <Box p={3}>
          {/* Logo & Status Section */}
          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, bgcolor: 'white', borderRadius: 2 }}
          >
            <Box display="flex" alignItems="center" gap={3}>
              <Box position="relative">
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fafafa'
                  }}
                >
                  <img
                    src={logoWrapper(logo, token)}
                    width={100}
                    alt={form?.name}
                    style={{ objectFit: 'contain' }}
                  />
                </Box>
                <input hidden id="dcn-logo" type="file" onChange={uploadLogo} />
                <label htmlFor="dcn-logo">
                  <IconButton
                    component="span"
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 5,
                      right: 5,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </label>
              </Box>

              <Box flex={1}>
                <Typography variant="h6" gutterBottom>
                  {form?.name || 'DCN Token'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Symbol: {form?.symbol || 'N/A'}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <Typography variant="body2" fontWeight={500}>
                    Status:
                  </Typography>
                  <Switch
                    checked={status}
                    onChange={(e) => setStatus(e.target.checked)}
                  />
                  <Chip
                    label={status ? 'Active' : 'Inactive'}
                    color={status ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* ========== ACCORDION SECTIONS ========== */}

          {/* Identity Section */}
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Description color="primary" />
                <Typography fontWeight={600}>Identity Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="DCN Name"
                  value={form?.name ?? ''}
                  onChange={handleChange('name')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Symbol"
                  value={form?.symbol ?? ''}
                  onChange={handleChange('symbol')}
                  size="small"
                />
                <SelectBlock
                  label="Reference Symbol (Price Source)"
                  value={form?.reference_symbol}
                  onChange={handleChange('reference_symbol')}
                  options={REFERENCE_SYMBOL_CODES}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Issuer Name"
                  value={form?.issuer ?? ''}
                  onChange={handleChange('issuer')}
                  size="small"
                />
                <SelectBlock
                  label="Currency"
                  value={form?.currency ?? 'USD'}
                  onChange={handleChange('currency')}
                  options={[
                    { code: 'USD', label: 'USD' },
                    { code: 'EUR', label: 'EUR' },
                    { code: 'GBP', label: 'GBP' },
                    { code: 'JPY', label: 'JPY' }
                  ]}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Economic Terms Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalance color="primary" />
                <Typography fontWeight={600}>
                  Economic Terms & Seniority
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Face Value"
                    value={form?.face_value ?? ''}
                    onChange={handleChange('face_value')}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="DCN Amount"
                    value={form?.dcn_amount ?? ''}
                    onChange={handleChange('dcn_amount')}
                    size="small"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Seniority (Text Description)"
                  value={form?.seniority ?? ''}
                  onChange={handleChange('seniority')}
                  size="small"
                  helperText="Optional legacy text field"
                />
                <SelectBlock
                  label="Seniority Code"
                  value={form?.seniority_code}
                  onChange={handleChange('seniority_code')}
                  options={SENIORITY_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Yield Structure Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp color="primary" />
                <Typography fontWeight={600}>Yield Structure</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Yield Type Code"
                  value={form?.yield_type_code}
                  onChange={handleChange('yield_type_code')}
                  options={YIELD_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
                <SelectBlock
                  label="Yield Rate Code"
                  value={form?.yield_rate_code}
                  onChange={(e) => {
                    const selectedCode = e.target.value;
                    const selectedYield = YIELD_RATE_CODES.find(
                      (y) => y.code === selectedCode
                    );
                    if (selectedYield) {
                      const yieldPercent = parseFloat(
                        selectedYield.label.replace('%', '')
                      );
                      setForm({
                        ...form,
                        yield_rate_code: selectedCode,
                        annual_yield: yieldPercent
                      });
                    }
                  }}
                  options={YIELD_RATE_CODES}
                  size="small"
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Annual Yield (%)"
                  value={form?.annual_yield ?? ''}
                  onChange={handleChange('annual_yield')}
                  helperText="Auto-populated from Yield Rate Code selection"
                  size="small"
                />
                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Daily Yield (%)"
                    value={dailyYield}
                    disabled
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Hourly Yield (%)"
                    value={hourlyYield}
                    disabled
                    size="small"
                  />
                </Box>
                <SelectBlock
                  label="YTM Type Code"
                  value={form?.ytm_type_code}
                  onChange={handleChange('ytm_type_code')}
                  options={YTM_CODES}
                  grouped={true}
                  size="small"
                />
                <SelectBlock
                  label="Yield Redemption Code"
                  value={form?.yield_redemption_code}
                  onChange={handleChange('yield_redemption_code')}
                  options={REDEMPTION_FREQUENCY_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* DCN Type & Maturity Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <DateRange color="primary" />
                <Typography fontWeight={600}>DCN Type & Maturity</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="DCN Type"
                  value={form?.type}
                  onChange={handleChange('type')}
                  options={[
                    { code: 'PDCN', label: 'PDCN (Perpetual DCN)' },
                    { code: 'FDCN', label: 'FDCN (Fixed-Term DCN)' }
                  ]}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Yield To Maturity Value"
                  value={yieldToMaturity}
                  disabled
                  helperText={
                    form?.type === 'PDCN'
                      ? 'Perpetual DCN has no maturity'
                      : 'Calculated based on face value, yield, and maturity date'
                  }
                  size="small"
                />
                <DateField
                  label="Maturity Date"
                  value={form?.type === 'PDCN' ? '' : form?.maturity_date}
                  onChange={handleChange('maturity_date')}
                  disabled={form?.type === 'PDCN'}
                  helperText={
                    form?.type === 'PDCN'
                      ? 'Not applicable for Perpetual DCN'
                      : ''
                  }
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Hold Periods & Early Redemption Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Lock color="primary" />
                <Typography fontWeight={600}>
                  Hold Periods & Early Redemption
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <DateField
                  label="Mandatory Hold End Date"
                  value={form?.mandatory_hold}
                  onChange={handleChange('mandatory_hold')}
                  size="small"
                />
                <SelectBlock
                  label="Mandatory Hold Code"
                  value={form?.mandatory_hold_code}
                  onChange={handleChange('mandatory_hold_code')}
                  options={MANDATORY_HOLD_CODES}
                  grouped={true}
                  size="small"
                />
                <Divider sx={{ my: 1 }} />
                <DateField
                  label="Early Redemption Date"
                  value={form?.early_redemption}
                  onChange={handleChange('early_redemption')}
                  size="small"
                />
                <SelectBlock
                  label="Early Redemption Code"
                  value={form?.early_redemption_code}
                  onChange={handleChange('early_redemption_code')}
                  options={EARLY_REDEMPTION_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Conversion Features Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <SwapHoriz color="primary" />
                <Typography fontWeight={600}>Conversion Features</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Conversion Mechanism Code"
                  value={form?.conversion_mechanism_code}
                  onChange={handleChange('conversion_mechanism_code')}
                  options={CONVERSION_MECHANISM_CODES}
                  grouped={true}
                  size="small"
                />
                <SelectBlock
                  label="Conversion Type Code"
                  value={form?.conversion_type_code}
                  onChange={handleChange('conversion_type_code')}
                  options={CONVERSION_WINDOW_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Compliance Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Gavel color="primary" />
                <Typography fontWeight={600}>
                  Compliance & Classification
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <SelectBlock
                    label="Grade Reference"
                    value={form?.grade_reference}
                    onChange={handleChange('grade_reference')}
                    options={GRADE_REFERENCE_CODES}
                    grouped={true}
                    size="small"
                  />
                  <SelectBlock
                    label="Exemption Reference"
                    value={form?.exemption_reference}
                    onChange={handleChange('exemption_reference')}
                    options={EXEMPTION_REFERENCE_CODES}
                    grouped={true}
                    size="small"
                  />
                </Box>
                <Box display="flex" gap={2}>
                  <SelectBlock
                    label="Region Reference"
                    value={form?.region_reference}
                    onChange={handleChange('region_reference')}
                    options={REGION_REFERENCE_CODES}
                    grouped={true}
                    size="small"
                  />
                  <SelectBlock
                    label="Sector Reference"
                    value={form?.sector_reference}
                    onChange={handleChange('sector_reference')}
                    options={SECTOR_REFERENCE_CODES}
                    grouped={true}
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Description Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Description color="primary" />
                <Typography fontWeight={600}>Additional Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Description"
                value={form?.description ?? ''}
                onChange={handleChange('description')}
                helperText="Detailed description of the DCN offering"
                size="small"
              />
            </AccordionDetails>
          </Accordion>

          {/* /crypto/updateToken writes PDCN/DCN/FDCN edits into dcn_tokens,
              so link against the 'DCN' canonical type to match. */}
          <TreasuryLockStatusFields tokenType="DCN" tokenId={form?.id} />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ bgcolor: 'white', px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}
      >
        <Button onClick={() => setOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={saveToken}
          sx={{
            bgcolor: 'success.main',
            '&:hover': { bgcolor: 'success.dark' },
            px: 4
          }}
        >
          Save DCN Token
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ===================== HELPERS ===================== */

const SelectBlock = ({
  label,
  value,
  onChange,
  options,
  grouped = false,
  size = 'medium'
}) => {
  if (!grouped) {
    return (
      <FormControl fullWidth size={size}>
        <InputLabel>{label}</InputLabel>
        <Select value={value ?? ''} label={label} onChange={onChange}>
          {options.map((o) => (
            <MenuItem key={o.code} value={o.code}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Group options by category for better organization
  const groupedOptions = options.reduce((acc, option) => {
    const category = option.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(option);
    return acc;
  }, {});

  return (
    <FormControl fullWidth size={size}>
      <InputLabel>{label}</InputLabel>
      <Select value={value ?? ''} label={label} onChange={onChange}>
        {Object.entries(groupedOptions).map(([category, items]) => [
          <ListSubheader
            key={`header-${category}`}
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            {category}
          </ListSubheader>,
          ...items.map((o) => (
            <MenuItem key={o.code} value={o.code} sx={{ pl: 4 }}>
              <Typography variant="body2">{o.label}</Typography>
            </MenuItem>
          ))
        ])}
      </Select>
    </FormControl>
  );
};

const DateField = ({
  label,
  value,
  onChange,
  disabled,
  helperText,
  size = 'medium'
}) => (
  <TextField
    fullWidth
    type="date"
    label={label}
    InputLabelProps={{ shrink: true }}
    value={value ?? ''}
    onChange={onChange}
    disabled={disabled}
    helperText={helperText}
    size={size}
  />
);

export default EditDCNToken;
